import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { DeleteAdminOrganizationResponse } from '@utils/types/admin';
import { ADMIN_ORGANIZATIONS_QUERY_KEY } from './use-create-admin-organization';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * Hook to delete (soft-delete) an on-prem organization
 *
 * DELETE /api/admin/on-prem/organizations/{id}
 */
export const useDeleteAdminOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const deleteOrganization = async (organizationId: number | string) => {
    const url = `api/admin/on-prem/organizations/${organizationId}`;
    const response = await Axios.delete<DeleteAdminOrganizationResponse>(url);
    return response.data;
  };

  return useMutation<
    DeleteAdminOrganizationResponse,
    AxiosError<BaseError>,
    number | string
  >(deleteOrganization, {
    onSuccess: () => {
      toast.success(t('organization-deleted-successfully'));
      queryClient.invalidateQueries([ADMIN_ORGANIZATIONS_QUERY_KEY]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-delete-organization');
      toast.error(String(errorMessage));
    }
  });
};
